import React from "react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { KD9LogoCircle, KD9Watermark } from "@/components/KD9Logo";
import { useLanguage } from "@/context/LanguageContext";
import {
  HeartHandshake,
  Scale,
  Users,
  BookOpen,
  Shield,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const Sobre = () => {
  const { t } = useLanguage();

  const VALORES = [
    { k: "val.humanidad", icon: HeartHandshake, color: "#1FA774" },
    { k: "val.etica", icon: Scale, color: "#D4AF37" },
    { k: "val.acompanamiento", icon: Users, color: "#3C78B5" },
    { k: "val.conocimiento", icon: BookOpen, color: "#0F4C81" },
    { k: "val.responsabilidad", icon: Shield, color: "#A67C00" },
    { k: "val.evolucion", icon: TrendingUp, color: "#4BC58F" },
  ];

  return (
    <div className="min-h-screen" data-testid="sobre-page">
      <PublicNav />

      {/* hero */}
      <section className="relative overflow-hidden">
        <KD9Watermark
          size={780}
          className="-right-40 -top-20"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 kd9-fade-up">
            <div
              className="font-mont text-[11px] tracking-[0.45em] uppercase mb-6"
              style={{ color: "var(--kd9-accent)" }}
              data-testid="sobre-kicker"
            >
              · {t("about.kicker")} ·
            </div>
            <h1
              className="font-cinzel kd9-gold-text leading-[1.05] mb-6"
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.5rem)", fontWeight: 700, letterSpacing: "0.02em" }}
              data-testid="sobre-title"
            >
              {t("about.title")}
            </h1>
            <p
              className="font-cormorant italic text-2xl mb-2"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              {t("hero.tagline")}
            </p>
          </div>
          <div className="lg:col-span-5 flex justify-center kd9-fade-up kd9-delay-2">
            <KD9LogoCircle size={360} />
          </div>
        </div>
      </section>

      {/* body */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
        <div
          className="kd9-card p-8 lg:p-12 text-center kd9-fade-up"
          data-testid="sobre-body"
        >
          <p
            className="font-cormorant text-2xl lg:text-3xl leading-relaxed max-w-3xl mx-auto"
            style={{ color: "var(--kd9-text)" }}
          >
            {t("about.body")}
          </p>
        </div>
      </section>

      {/* misión + visión */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 grid lg:grid-cols-2 gap-6">
        {[
          { title: t("about.mision.title"), body: t("about.mision.body"), accent: "#D4AF37" },
          { title: t("about.vision.title"), body: t("about.vision.body"), accent: "#0F4C81" },
        ].map((card, i) => (
          <div
            key={card.title}
            className={`kd9-card p-8 lg:p-10 kd9-fade-up kd9-delay-${i + 1}`}
            data-testid={`sobre-${card.title.toLowerCase()}`}
          >
            <div
              className="w-14 h-1 rounded-full mb-6"
              style={{ background: card.accent, boxShadow: `0 0 18px ${card.accent}` }}
            />
            <h2
              className="font-cinzel kd9-gold-text text-3xl tracking-[0.1em] mb-4"
            >
              {card.title.toUpperCase()}
            </h2>
            <p
              className="font-cormorant text-xl leading-relaxed"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              {card.body}
            </p>
          </div>
        ))}
      </section>

      {/* valores */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="text-center mb-10">
          <h2 className="font-cinzel kd9-gold-text text-3xl md:text-4xl tracking-[0.18em] kd9-arrow inline-flex items-center">
            {t("about.valores").toUpperCase()}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {VALORES.map((v, i) => (
            <div
              key={v.k}
              className={`kd9-card p-6 text-center kd9-fade-up kd9-delay-${(i % 6) + 1}`}
              data-testid={`valor-${v.k}`}
            >
              <div
                className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{
                  background: `${v.color}22`,
                  border: `1px solid ${v.color}55`,
                  boxShadow: `0 0 24px -8px ${v.color}55`,
                }}
              >
                <v.icon size={22} style={{ color: v.color }} />
              </div>
              <div
                className="font-cinzel kd9-gold-text text-lg tracking-[0.16em]"
              >
                {t(v.k).toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div
          className="kd9-card p-10 lg:p-14 text-center"
          data-testid="sobre-cta"
        >
          <p
            className="font-cormorant italic text-2xl max-w-2xl mx-auto mb-8"
            style={{ color: "var(--kd9-text)" }}
          >
            &ldquo;{t("footer.lema")}&rdquo;
          </p>
          <Link
            to="/contacto"
            className="kd9-btn-primary px-8 py-4 rounded-xl text-[12px] inline-flex items-center gap-3"
            data-testid="sobre-contacto-btn"
          >
            {t("nav.contacto")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Sobre;
